import React, { useState } from 'react';
import { Shield, Target, Zap, AlertTriangle, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { StatusIndicator } from '../ui/StatusIndicator';
import { validateAuthInputs } from '../../utils/validation';
import logoImage from '../../assets/file_000000005d0462309eb434f4f4fd873d copy.png';

interface WelcomePageProps {
  onSignIn: (email: string, password: string) => Promise<void>;
  onSignUp: (email: string, password: string) => Promise<void>;
  loading?: boolean;
  error?: string;
}

export const WelcomePage: React.FC<WelcomePageProps> = ({
  onSignIn,
  onSignUp,
  loading = false,
  error
}) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);

    const validation = validateAuthInputs(email, password);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    try {
      if (isSignUp) {
        await onSignUp(email, password);
      } else {
        await onSignIn(email, password);
      }
    } catch (err) {
      // Error is handled by the parent component
    }
  };

  const allErrors = [...validationErrors, ...(error ? [error] : [])];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-red-900 flex flex-col justify-center p-4 relative">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mx-auto">
        <div className="text-white space-y-8">
          <div className="flex items-center space-x-4 mb-8">
            <img 
              src={logoImage}
              alt="Red Set Logo" 
              className="h-16 w-16"
            />
            <div>
              <h1 className="text-5xl font-bold text-white">Red Set</h1>
              <p className="text-xl text-red-400">AI Security Assessment Platform</p>
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="text-3xl font-bold mb-6">
              Autonomous Adversarial AI Auditing
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              Deploy specialized red team agents to probe AI systems for vulnerabilities. 
              Conduct comprehensive security assessments that evaluate jailbreak resistance, 
              bias patterns, toxicity controls, and response integrity.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
              <div className="flex items-center space-x-3 bg-black/40 p-4 rounded-lg backdrop-blur-sm border border-red-500/30">
                <Zap className="h-6 w-6 text-red-400 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Live Probing</h3>
                  <p className="text-sm text-gray-400">Real-time vulnerability assessment</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 bg-black/40 p-4 rounded-lg backdrop-blur-sm border border-red-500/30">
                <Target className="h-6 w-6 text-red-400 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Multi-Vector Testing</h3>
                  <p className="text-sm text-gray-400">5 specialized attack personas</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 bg-black/40 p-4 rounded-lg backdrop-blur-sm border border-red-500/30">
                <Shield className="h-6 w-6 text-red-400 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Comprehensive Analysis</h3>
                  <p className="text-sm text-gray-400">Detailed vulnerability scoring</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 bg-black/40 p-4 rounded-lg backdrop-blur-sm border border-red-500/30">
                <AlertTriangle className="h-6 w-6 text-red-400 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Risk Assessment</h3>
                  <p className="text-sm text-gray-400">Actionable security insights</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-900/50 to-black/50 p-6 rounded-lg border border-red-500/30 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-white mb-2">Professional Security Testing</h3>
            <p className="text-gray-300">
              Designed for security researchers, AI safety teams, and organizations 
              implementing responsible AI deployment protocols.
            </p>
          </div>
        </div>

        <div className="flex justify-center">
          <Card variant="red" className="w-full max-w-md">
            <div className="text-center mb-6">
              <Lock className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {isSignUp ? 'Create Account' : 'Access Red Set'}
              </h2>
              <p className="text-gray-600">
                {isSignUp 
                  ? 'Join the adversarial AI testing platform' 
                  : 'Sign in to deploy red team agents'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="security@company.com"
                  icon={<Mail className="h-4 w-4" />}
                  required
                />
              </div>

              <div>
                <div className="relative">
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={setPassword}
                    placeholder={isSignUp ? "Min 8 chars, uppercase, lowercase, number/symbol" : "Enter your password"}
                    icon={<Lock className="h-4 w-4" />}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-9 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {allErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm space-y-1">
                  {allErrors.map((err, index) => (
                    <div key={index}>{err}</div>
                  ))}
                </div>
              )}

              {loading && (
                <StatusIndicator 
                  status="loading" 
                  message={isSignUp ? "Creating account..." : "Signing in..."} 
                />
              )}

              <Button
                type="submit"
                loading={loading}
                className="w-full"
                size="lg"
                disabled={!email || !password}
              >
                <Target className="h-5 w-5" />
                <span>{isSignUp ? 'Create Account' : 'Access Platform'}</span>
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                >
                  {isSignUp 
                    ? 'Already have an account? Sign in' 
                    : "Don't have an account? Sign up"
                  }
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-red-500/20">
              <div className="bg-red-50 p-4 rounded-lg border border-red-500/30">
                <h4 className="font-medium text-red-900 mb-2 flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Security Notice
                </h4>
                <p className="text-xs text-red-800">
                  This platform is designed for authorized security testing and research purposes. 
                  Users are responsible for ensuring compliance with applicable laws and regulations.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="text-red-400 font-bold text-lg">Red Set. Deploy.</p>
        <p className="text-gray-400 text-sm">Autonomous • Adversarial • Comprehensive</p>
      </div>
    </div>
  );
};